import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, Mail, Phone, Calendar, CreditCard, FileText, 
  Bookmark, Upload, CheckCircle, Clock, XCircle
} from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export default function MemberDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [memberProfile, setMemberProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'member') {
      // If not a member, redirect
      if (user?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/login');
      }
      return;
    }
    
    fetchMemberProfile();
  }, [user, navigate]);

  const fetchMemberProfile = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.get(`${API_BASE_URL}/api/membership/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
    if (status === 'expired') {
      return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Expired</Badge>;
    }
    return <Badge>{status}</Badge>;
  };

  const getVerificationBadge = (status) => {
    if (status === 'approved') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending Verification</Badge>;
    }
    if (status === 'rejected') {
      return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    }
    return <Badge>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!memberProfile) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Card className="p-8 text-center">
            <p className="text-gray-600 mb-4">Member profile not found</p>
            <Button onClick={() => navigate('/membership')}>View Membership Plans</Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      {/* Add hero section for header visibility */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 pt-32 pb-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold text-white">Member Dashboard</h1>
          <p className="text-gray-200 mt-2">Manage your membership and access exclusive resources</p>
        </div>
      </div>
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Profile & Membership */}
            <div className="lg:col-span-2 space-y-6">
              {/* Profile Card */}
              <Card className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-[#1E3A5F] mb-2">Profile Information</h2>
                    <p className="text-sm text-gray-600">
                      Member since {new Date(memberProfile.created_at).toLocaleDateString('en-IN', { 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                  <Button variant="outline" size="sm">Edit Profile</Button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">{memberProfile.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{memberProfile.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium">{memberProfile.phone}</p>
                    </div>
                  </div>

                  {memberProfile.member_type === 'individual' ? (
                    <>
                      {memberProfile.profession && (
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">Profession</p>
                            <p className="font-medium">{memberProfile.profession}</p>
                          </div>
                        </div>
                      )}
                      {memberProfile.area_of_expertise && (
                        <div className="flex items-center gap-3">
                          <Bookmark className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">Area of Expertise</p>
                            <p className="font-medium">{memberProfile.area_of_expertise}</p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Organization</p>
                          <p className="font-medium">{memberProfile.organization_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Bookmark className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Organization Type</p>
                          <p className="font-medium">{memberProfile.organization_type}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </Card>

              {/* Membership Status Card */}
              <Card className="p-6">
                <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">Membership Status</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Current Plan</p>
                      <p className="font-bold text-lg text-[#E87722]">
                        {memberProfile.plan_name || 'No active plan'}
                      </p>
                    </div>
                    {getStatusBadge(memberProfile.subscription_status)}
                  </div>

                  {memberProfile.subscription_start_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Start Date</p>
                        <p className="font-medium">
                          {new Date(memberProfile.subscription_start_date).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                    </div>
                  )}

                  {memberProfile.subscription_end_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Renewal Date</p>
                        <p className="font-medium">
                          {new Date(memberProfile.subscription_end_date).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                    </div>
                  )}

                  {memberProfile.member_type === 'institutional' && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <p className="text-sm text-gray-600">Verification Status</p>
                        <p className="font-medium mt-1">
                          {memberProfile.verification_status === 'pending' && 
                            'Your account is under review. You\'ll be notified once verified.'}
                          {memberProfile.verification_status === 'approved' && 
                            'Your organization has been verified.'}
                          {memberProfile.verification_status === 'rejected' && 
                            'Verification failed. Please contact support.'}
                        </p>
                      </div>
                      {getVerificationBadge(memberProfile.verification_status)}
                    </div>
                  )}
                </div>

                {memberProfile.subscription_status === 'pending' && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Payment Pending:</strong> Complete your payment to activate your membership.
                    </p>
                    <Button className="mt-3 bg-[#E87722] hover:bg-[#d66a1a]" size="sm">
                      Complete Payment
                    </Button>
                  </div>
                )}

                {memberProfile.subscription_status === 'active' && (
                  <Button variant="outline" className="w-full mt-6">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Manage Subscription
                  </Button>
                )}
              </Card>
            </div>

            {/* Right Column - Quick Actions */}
            <div className="space-y-6">
              {/* Quick Links */}
              <Card className="p-6">
                <h3 className="font-bold text-lg mb-4">Quick Access</h3>
                <div className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/programs')}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Browse Programs
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/research-library')}
                  >
                    <Bookmark className="h-4 w-4 mr-2" />
                    Research Library
                  </Button>
                  {memberProfile.member_type === 'institutional' && 
                   memberProfile.verification_status === 'pending' && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate('/membership/documents')}
                      data-testid="member-upload-documents-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Documents
                    </Button>
                  )}
                </div>
              </Card>

              {/* Member Benefits */}
              <Card className="p-6">
                <h3 className="font-bold text-lg mb-4">Your Benefits</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span>Full access to Government Schemes Platform</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span>Save & bookmark schemes and research</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span>Personalized recommendations</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <span>Member communications & updates</span>
                  </div>
                </div>
              </Card>

              {/* Support */}
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                <h3 className="font-bold text-lg mb-2">Need Help?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Our support team is here to assist you
                </p>
                <Button variant="outline" size="sm" className="w-full">
                  Contact Support
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
